import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { Dialog, Drawer } from '@mui/material';
import Url from '../../data/url';
import { AGREEMENT, ACCESS_TOKEN_KEY, REFRESH_TOKEN_KEY } from '../../data/define';
import { useEditMyInfoMutation, useHardDeleteMutation } from '../../queries';
import IcRightArrow from '../../assets/image/ic_chevron_right.svg';
import CheckBox from '../common/CheckBox';
import AgreementTextAge from './AgreementTextAge';
import AgreementTextService from './AgreementTextService';
import AgreementTextPrivacy from './AgreementTextPrivacy';
import AgreementTextEvent from './AgreementTextEvent';
import { useTranslation } from '../../hook/useTranslation';

function AgreementDialog({ openType = false, onClose = () => {} }) {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [checkedIds, setCheckedIdsList] = useState([]);
  const [showDetailText, setShowDetailText] = useState(false);
  const [agreementText, setAgreementText] = useState(null);

  const { mutate: editMyInfo } = useEditMyInfoMutation();
  const { mutate: hardDelete } = useHardDeleteMutation();

  const handleAllCheck = async (checked) => {
    if (checked) {
      setCheckedIdsList([AGREEMENT.AGE, AGREEMENT.SERVICE, AGREEMENT.PRIVACY, AGREEMENT.EVENT]);
    } else {
      setCheckedIdsList([]);
    }
  };

  // 유닛 개별 선택시 단어들 호출
  const handleSingleCheck = async (checked, id) => {
    if (checked) {
      setCheckedIdsList((prev) => [...prev, id]);
    } else {
      setCheckedIdsList((prev) => {
        return prev.filter((item) => item !== id);
      });
    }
  };

  const handleAuthPermission = useCallback(() => {
    if (checkedIds.includes(AGREEMENT.EVENT)) {
      const apiParams = {
        agreeEventBenefit: true,
      };
      editMyInfo(apiParams, {
        onSuccess: () => {
          onClose();
        },
        onError: (error) => {
          toast.error(t('agreement.toast_fail'));
          console.error('약관 동의 실패:', error);
        },
      });
    } else {
      onClose();
    }
  }, [checkedIds, editMyInfo, onClose, t]);

  const handleDisagree = () => {
    hardDelete(null, {
      onSuccess: () => {
        onClose();
        localStorage.removeItem(ACCESS_TOKEN_KEY);
        localStorage.removeItem(REFRESH_TOKEN_KEY);
        navigate(Url.Login, { replace: true });
      },
      onError: (error) => {
        toast.error(t('agreement.toast_disagree_fail'));
        console.error('약관 부동의 실패:', error);
      },
    });
  };

  const handleShowText = (id) => {
    setShowDetailText(true);
    setAgreementText(id);
  };

  useEffect(() => {
    if (!openType) {
      setCheckedIdsList([]);
      setShowDetailText(false);
      setAgreementText(null);
    }
  }, [openType]);

  return (
    <>
      <Dialog
        open={openType && !showDetailText}
        onClose={(reason: any) => {
          if (reason === 'backdropClick') return;
          onClose();
        }}
        PaperProps={{
          sx: {
            borderRadius: '16px',
            maxWidth: 'calc(100vw - 40px)',
            width: '100%',
            margin: '20px',
          },
        }}
        sx={{
          '& .MuiBackdrop-root': {
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
          },
        }}
      >
        <div className="w-full bg-white rounded-[16px] p-[24px] flex flex-col gap-[40px]">
          <div className="w-full flex flex-col gap-[24px]">
            <div className="w-full flex items-center justify-between">
              <div className="text-[20px] font-[700] text-[#1C1C1C]">{t('login.agreement.title')}</div>
            </div>
            <div className="w-full flex flex-col gap-[20px]">
              <div className="bg-[#F8F8F8] p-[16px] rounded-[8px] h-[56px] flex items-center">
                <CheckBox
                  value="all"
                  label={t('login.agreement.all')}
                  isChecked={checkedIds?.length === 4 ? true : false}
                  onChange={handleAllCheck}
                  boxStyle={{ padding: 0 }}
                  labelStyle={{ fontWeight: '700', fontSize: '16px', color: '#363636' }}
                />
              </div>
              <div className="flex flex-col gap-[16px] px-[2px]">
                <div className="w-full flex items-center gap-[20px]">
                  <div className="flex-1 flex items-center gap-[12px]">
                    <CheckBox
                      id={AGREEMENT.AGE}
                      value={AGREEMENT.AGE}
                      label={t('login.agreement.age')}
                      isChecked={checkedIds.includes(AGREEMENT.AGE)}
                      onChange={handleSingleCheck}
                      labelStyle={{ fontSize: '14px', color: '#363636' }}
                    />
                  </div>
                  <button
                    type="button"
                    className="cursor-pointer flex items-center justify-center"
                    onClick={() => handleShowText(AGREEMENT.AGE)}
                  >
                    <img src={IcRightArrow} alt="right" className="w-[24px] h-[24px]" />
                  </button>
                </div>
                <div className="w-full flex items-center gap-[20px]">
                  <div className="flex-1 flex items-center gap-[12px]">
                    <CheckBox
                      id={AGREEMENT.SERVICE}
                      value={AGREEMENT.SERVICE}
                      label={t('login.agreement.service')}
                      isChecked={checkedIds.includes(AGREEMENT.SERVICE)}
                      onChange={handleSingleCheck}
                      labelStyle={{ fontSize: '14px', color: '#363636' }}
                    />
                  </div>
                  <button
                    type="button"
                    className="cursor-pointer flex items-center justify-center"
                    onClick={() => handleShowText(AGREEMENT.SERVICE)}
                  >
                    <img src={IcRightArrow} alt="right" className="w-[24px] h-[24px]" />
                  </button>
                </div>
                <div className="w-full flex items-center gap-[20px]">
                  <div className="flex-1 flex items-center gap-[12px]">
                    <CheckBox
                      id={AGREEMENT.PRIVACY}
                      value={AGREEMENT.PRIVACY}
                      label={t('login.agreement.privacy')}
                      isChecked={checkedIds.includes(AGREEMENT.PRIVACY)}
                      onChange={handleSingleCheck}
                      labelStyle={{ fontSize: '14px', color: '#363636' }}
                    />
                  </div>
                  <button
                    type="button"
                    className="cursor-pointer flex items-center justify-center"
                    onClick={() => handleShowText(AGREEMENT.PRIVACY)}
                  >
                    <img src={IcRightArrow} alt="right" className="w-[24px] h-[24px]" />
                  </button>
                </div>
                <div className="w-full flex items-center gap-[20px]">
                  <div className="flex-1 flex items-center gap-[12px]">
                    <CheckBox
                      id={AGREEMENT.EVENT}
                      value={AGREEMENT.EVENT}
                      label={t('login.agreement.event')}
                      isChecked={checkedIds.includes(AGREEMENT.EVENT)}
                      onChange={handleSingleCheck}
                      labelStyle={{ fontSize: '14px', color: '#363636' }}
                    />
                  </div>
                  <button
                    type="button"
                    className="cursor-pointer flex items-center justify-center"
                    onClick={() => handleShowText(AGREEMENT.EVENT)}
                  >
                    <img src={IcRightArrow} alt="right" className="w-[24px] h-[24px]" />
                  </button>
                </div>
              </div>
            </div>
          </div>
          <div className="w-full flex flex-col gap-[12px]">
            <button
              type="button"
              disabled={
                !checkedIds.includes(AGREEMENT.AGE) ||
                !checkedIds.includes(AGREEMENT.SERVICE) ||
                !checkedIds.includes(AGREEMENT.PRIVACY)
              }
              onClick={handleAuthPermission}
              className="w-full h-[56px] rounded-[8px] flex items-center justify-center text-[18px] font-[700] tracking-[-0.36px] disabled:bg-[#F3F3F3] disabled:text-[#969696] bg-[#5EBEBB] text-white"
            >
              {t('login.agreement.btn_start')}
            </button>
            <div className="w-full flex items-center justify-center">
              <button
                type="button"
                onClick={handleDisagree}
                className="text-[16px] font-[400] text-[#767676] underline tracking-[-0.32px]"
              >
                {t('login.agreement.btn_disagree')}
              </button>
            </div>
          </div>
        </div>
      </Dialog>
      <Drawer
        open={showDetailText}
        anchor="right"
        onClose={() => setShowDetailText(false)}
        PaperProps={{
          sx: {
            width: '100vw',
            maxWidth: '100vw',
          },
        }}
        sx={{
          zIndex: 9999,
        }}
      >
        <div className="w-full h-full bg-white overflow-hidden p-5">
          {agreementText === AGREEMENT.AGE && (
            <AgreementTextAge onClose={() => setShowDetailText(false)} />
          )}
          {agreementText === AGREEMENT.SERVICE && (
            <AgreementTextService onClose={() => setShowDetailText(false)} />
          )}
          {agreementText === AGREEMENT.PRIVACY && (
            <AgreementTextPrivacy onClose={() => setShowDetailText(false)} />
          )}
          {agreementText === AGREEMENT.EVENT && (
            <AgreementTextEvent onClose={() => setShowDetailText(false)} />
          )}
        </div>
      </Drawer>
    </>
  );
}

export default AgreementDialog;
